import React, { useCallback, useEffect, useMemo, useState } from 'react';
import adminAPI from '../services/adminAPI';
import { companiesAPI, dashboardsAPI } from '../services/api';
import useStore from '../store/useStore';
import UserDrawer from './users/UserDrawer';
import { ROLES, roleLabel, displayName, initials, lastSignIn, dashboardAccess, companyAccess } from './users/userFormat';
import '../styles/DashboardEditor.css';
import '../styles/UserManagement.css';

const STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

/**
 * Who can sign in and what they can see. The table is for reading and finding
 * people; choosing a row opens the side panel where all changes are made.
 */
const UserManagement = () => {
  const currentUser = useStore((state) => state.user);
  const userCompanies = useStore((state) => state.companies);
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [allCompanies, setAllCompanies] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('all');
  const [company, setCompany] = useState('all');
  const [status, setStatus] = useState('all');
  const [editing, setEditing] = useState(null); // null | 'new' | a user
  const [notice, setNotice] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      const res = await adminAPI.users.getAll();
      if (!res?.success) throw new Error(res?.error || 'Could not load users');
      setUsers(res.data || []);
      setLoadError('');
    } catch (err) {
      setLoadError(err.response?.data?.error || err.message || 'Could not load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    dashboardsAPI.getAll().then((res) => { if (res?.success) setDashboards(res.data || []); }).catch(() => {});
    if (isSuperAdmin) companiesAPI.getAll().then((res) => { if (res?.success) setAllCompanies(res.data || []); }).catch(() => {});
  }, [loadUsers, isSuperAdmin]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), notice.kind === 'error' ? 8000 : 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  const companies = useMemo(() => (isSuperAdmin ? allCompanies : userCompanies) || [], [isSuperAdmin, allCompanies, userCompanies]);

  const counts = useMemo(() => ({
    total: users.length,
    admins: users.filter((u) => u.role !== 'user').length,
    inactive: users.filter((u) => !u.is_active).length,
    never: users.filter((u) => u.is_active && !u.last_login_at).length,
  }), [users]);

  const term = search.trim().toLowerCase();
  const shown = users.filter((u) => {
    if (role !== 'all' && u.role !== role) return false;
    if (status !== 'all' && (status === 'active') !== Boolean(u.is_active)) return false;
    if (company !== 'all' && u.role !== 'super_admin' && !(u.companies || []).some((c) => String(c.id) === company)) return false;
    return !term || `${u.full_name || ''} ${u.username} ${u.email}`.toLowerCase().includes(term);
  });

  const filtered = term || role !== 'all' || company !== 'all' || status !== 'all';
  const clearFilters = () => { setSearch(''); setRole('all'); setCompany('all'); setStatus('all'); };
  const roleOptions = ROLES.filter((r) => r.value !== 'super_admin' || isSuperAdmin);

  const onSaved = async (message, kind = 'success') => {
    setEditing(null);
    setNotice({ message, kind });
    await loadUsers();
  };

  return (
    <div className="um-page">
      <header className="um-header">
        <div>
          <h1>Users</h1>
          <p>Control who can sign in and what they can see.</p>
        </div>
        <button type="button" className="btn-primary um-inline-button" onClick={() => setEditing('new')}>Add user</button>
      </header>

      {!loading && !loadError && users.length > 0 && (
        <dl className="um-summary">
          <div><dt>Users</dt><dd>{counts.total}</dd></div>
          <div><dt>Admins</dt><dd>{counts.admins}</dd></div>
          <div><dt>Inactive</dt><dd>{counts.inactive}</dd></div>
          <div><dt>Never signed in</dt><dd>{counts.never}</dd></div>
        </dl>
      )}

      <div className="um-panel">
        <div className="um-filters">
          <input type="search" className="um-search" placeholder="Search by name, username or email" aria-label="Search users" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select aria-label="Filter by role" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="all">All roles</option>
            {roleOptions.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {companies.length > 1 && (
            <select aria-label="Filter by company" value={company} onChange={(e) => setCompany(e.target.value)}>
              <option value="all">All companies</option>
              {companies.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </select>
          )}
          <div className="um-status-filter" role="group" aria-label="Filter by status">
            {STATUSES.map((s) => (
              <button key={s.value} type="button" aria-pressed={status === s.value} className={status === s.value ? 'active' : ''} onClick={() => setStatus(s.value)}>{s.label}</button>
            ))}
          </div>
        </div>

        {loadError ? (
          <div className="um-empty" role="alert">
            <p>{loadError}</p>
            <button type="button" className="btn-secondary um-inline-button" onClick={() => { setLoading(true); loadUsers(); }}>Try again</button>
          </div>
        ) : loading ? (
          <div className="um-empty" role="status">Loading users…</div>
        ) : shown.length === 0 ? (
          <div className="um-empty">
            {filtered ? (
              <>
                <p>No users match your search and filters.</p>
                <button type="button" className="btn-secondary um-inline-button" onClick={clearFilters}>Clear search and filters</button>
              </>
            ) : (
              <>
                <p>There are no users yet.</p>
                <button type="button" className="btn-primary um-inline-button" onClick={() => setEditing('new')}>Add user</button>
              </>
            )}
          </div>
        ) : (
          <table className="um-table">
            <thead>
              <tr>
                <th scope="col">User</th>
                <th scope="col">Role</th>
                <th scope="col">Access</th>
                <th scope="col">Last sign-in</th>
                <th scope="col">Status</th>
                <th scope="col"><span className="visually-hidden">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {shown.map((u) => (
                <tr key={u.id} className={u.is_active ? '' : 'um-row--inactive'} onClick={() => setEditing(u)}>
                  <td data-label="User">
                    <div className="um-identity">
                      <span className="um-avatar" aria-hidden="true">{initials(u)}</span>
                      <div className="um-identity-text">
                        <strong>{displayName(u)}{u.id === currentUser?.id && <span className="um-you">You</span>}</strong>
                        <small>{u.email}</small>
                      </div>
                    </div>
                  </td>
                  <td data-label="Role"><span className={`um-role-badge um-role-badge--${u.role}`}>{roleLabel(u.role)}</span></td>
                  <td data-label="Access">
                    <div>
                      <span className="um-access">{companyAccess(u)}</span>
                      <small className={`um-access-detail ${u.role === 'user' && !u.dashboard_count ? 'um-access-detail--warn' : ''}`}>{dashboardAccess(u)}</small>
                    </div>
                  </td>
                  <td data-label="Last sign-in" title={u.last_login_at ? new Date(u.last_login_at).toLocaleString() : undefined}>{lastSignIn(u.last_login_at)}</td>
                  <td data-label="Status"><span className={`um-status um-status--${u.is_active ? 'active' : 'inactive'}`}>{u.is_active ? 'Active' : 'Inactive'}</span>{u.is_active && !u.two_factor && <span className="um-tag" title="They will set up two-step verification when they next sign in">No two-step yet</span>}</td>
                  <td className="um-actions">
                    <button type="button" className="btn-secondary-small" aria-label={`Edit ${displayName(u)}`} onClick={(e) => { e.stopPropagation(); setEditing(u); }}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && !loadError && filtered && shown.length > 0 && (
        <p className="um-result-count">Showing {shown.length} of {users.length} users</p>
      )}

      {editing && (
        <UserDrawer
          key={editing === 'new' ? 'new' : editing.id}
          user={editing === 'new' ? null : editing}
          companies={companies}
          dashboards={dashboards}
          currentUser={currentUser}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}

      {notice && <div className={`editor-toast ${notice.kind === 'error' ? 'editor-toast--error' : ''}`} role="status">{notice.message}</div>}
    </div>
  );
};

export default UserManagement;
